<template>
    <div class="congress-schedule-event-card">
        <!-- Collapsible Header -->
        <div class="congress-schedule-event-card-header" @click="toggleExpanded">
            <div class="congress-schedule-event-card-header-inner">
                <div class="congress-schedule-event-card-header-inner-left">
                    <icon
                        icon="phosphor/regular/arrows-out-cardinal"
                        class="congress-schedule-event-card-header-move cursor-grab"
                    ></icon>
                    <span v-if="eventType === 'schedule'" class="congress-schedule-event-card-header-time">
                        {{ event.time }}
                    </span>
                </div>
                <div class="congress-schedule-event-card-header-label">
                    {{ event.title || lang('congresses::congresses.schedule.untitled_event') }}
                </div>
                <div class="congress-schedule-event-card-header-actions">
                    <Button type="button" size="sm" color="danger" variant="inline" @click="$emit('remove')">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/trash"></Icon>
                        </span>
                        <span class="action-label">
                            {{ lang('congresses::congresses.schedule.remove_event') }}
                        </span>
                    </Button>
                    <icon
                        icon="hero/outline/chevron-down"
                        class="congress-schedule-event-card-header-actions-icon"
                        :class="{ 'rotate-180': expanded }"
                    ></icon>
                </div>
            </div>
        </div>

        <!-- Expanded Content -->
        <div v-show="expanded" class="event-content mt-2 space-y-2">
            <!-- Common Fields -->
            <div class="grid grid-cols-1 px-5 gap-5">
                <Field
                    v-model="event.title"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.schedule.title'),
                        slug: `schedule.${props.dayIndex}.events.${props.eventIndex}.title`,
                        type: 'text',
                        adminComponent: 'AdminText',
                        required: true,
                    }"
                >
                    <template #help>
                        <small>
                            {{ lang('congresses::congresses.schedule.title_help') }}
                        </small>
                    </template>
                </Field>
            </div>

            <!-- Schedule Type Fields -->
            <template v-if="eventType === 'schedule'">
                <div class="grid grid-cols-2 gap-5 px-5">
                    <Field
                        v-model="event.time"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.time'),
                            slug: `schedule.${props.dayIndex}.events.${props.eventIndex}.time`,
                            type: 'text',
                            adminComponent: 'AdminText',
                            required: true,
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.time_help') }}
                            </small>
                        </template>
                    </Field>
                    <Field
                        v-model="event.location"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.location'),
                            slug: `schedule.${props.dayIndex}.events.${props.eventIndex}.location`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.location_help') }}
                            </small>
                        </template>
                    </Field>
                </div>

                <!-- Speakers Repeater -->
                <div class="grid grid-cols-1 px-5 gap-5">
                    <Field
                        v-model="event.speakers"
                        size="sm"
                        label-position="top"
                        :minus-icon="'phosphor/regular/trash'"
                        :field="{
                            name: lang('congresses::congresses.schedule.speakers'),
                            slug: `schedule.${props.dayIndex}.events.${props.eventIndex}.speakers`,
                            type: 'repeater',
                            adminComponent: 'AdminRepeater',
                            sortable: true,
                            fields: speakerFields,
                        }"
                    />
                </div>
            </template>

            <!-- Program Type Fields -->
            <template v-else-if="eventType === 'program'">
                <div class="grid grid-cols-1 px-5 gap-5">
                    <Field
                        v-model="event.image_media_id"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.image'),
                            slug: `image_media_id_${event._id}`,
                            type: 'file',
                            adminComponent: 'AdminFile',
                            accept: 'image/*',
                        }"
                    />
                </div>

                <div class="grid grid-cols-1 px-5 gap-5">
                    <Field
                        v-model="event.description"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.description'),
                            slug: `description_${event._id}`,
                            type: 'textarea',
                            adminComponent: 'AdminTextarea',
                        }"
                    />
                </div>

                <!-- Schedule Items (array of strings) -->
                <div class="grid grid-cols-1 px-5 gap-5">
                    <ScheduleItems v-model="event.schedule" />
                </div>

                <div class="grid grid-cols-3 gap-5 px-5">
                    <Field
                        v-model="event.price"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.price'),
                            slug: `price_${event._id}`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.price_help') }}
                            </small>
                        </template>
                    </Field>
                    <Field
                        v-model="event.capacity"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.capacity'),
                            slug: `capacity_${event._id}`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.capacity_help') }}
                            </small>
                        </template>
                    </Field>
                </div>

                <div class="grid grid-cols-2 gap-5 px-5">
                    <Field
                        v-model="event.location"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.location'),
                            slug: `location_${event._id}`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.location_help') }}
                            </small>
                        </template>
                    </Field>
                    <Field
                        v-model="event.location_address"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.location_address'),
                            slug: `location_address_${event._id}`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.location_address_help') }}
                            </small>
                        </template>
                    </Field>
                </div>

                <!--

                    <Field
                        v-model="event.currency"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.currency'),
                            slug: `currency_${event._id}`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    />
                -->

                <div class="grid grid-cols-2 gap-5 px-5">
                    <Field
                        v-model="event.date_display"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.date_display'),
                            slug: `date_display_${event._id}`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.date_display_help') }}
                            </small>
                        </template>
                    </Field>
                    <Field
                        v-model="event.time_display"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.time_display'),
                            slug: `time_display_${event._id}`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.time_display_help') }}
                            </small>
                        </template>
                    </Field>
                </div>

                <!-- Ticket Restrictions -->
                <div v-if="ticketOptions.length > 0" class="grid grid-cols-1 px-5 gap-5">
                    <Field
                        v-model="event.ticket_ids"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.ticket_restrictions'),
                            slug: `ticket_ids_${event._id}`,
                            type: 'select',
                            adminComponent: 'AdminSelect',
                            multiple: true,
                            options: ticketOptions,
                            placeholder: lang('congresses::congresses.schedule.no_ticket_restriction'),
                            portal: true,
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.ticket_restrictions_help') }}
                            </small>
                        </template>
                    </Field>

                    <Field
                        v-model="event.forbidden_ticket_ids"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.schedule.forbidden_ticket_restrictions'),
                            slug: `forbidden_ticket_ids_${event._id}`,
                            type: 'select',
                            adminComponent: 'AdminSelect',
                            multiple: true,
                            options: ticketOptions,
                            placeholder: lang('congresses::congresses.schedule.no_forbidden_ticket_restriction'),
                            portal: true,
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.schedule.forbidden_ticket_restrictions_help') }}
                            </small>
                        </template>
                    </Field>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { lang } from '@/lang/composable';
import Button from '@src/Common/Components/Button.vue';
import Field from '@src/Common/Components/Field.vue';
import ScheduleItems from './ScheduleItems.vue';
import type { ScheduleEvent } from '../types';
import type { Ticket } from '../types';

const props = defineProps<{
    eventType: 'schedule' | 'program';
    dayIndex: number;
    eventIndex: number;
    availableTickets?: Ticket[];
}>();

// Computed options for ticket select
const ticketOptions = computed(() =>
    (props.availableTickets || []).map((t) => ({
        id: t.id,
        name: t.name,
    })),
);

// eslint-disable-next-line
const emit = defineEmits<{
    (e: 'remove'): void;
}>();

const event = defineModel<ScheduleEvent>();

const expanded = ref(false);

const toggleExpanded = () => {
    expanded.value = !expanded.value;
};

// Speaker field configuration
const speakerFields = computed(() => ({
    name: {
        name: lang('congresses::congresses.schedule.speaker_name'),
        slug: 'name',
        type: 'text',
        adminComponent: 'AdminText',
        required: true,
        display_repeater_header: true,
    },
    role: {
        name: lang('congresses::congresses.schedule.speaker_role'),
        slug: 'role',
        type: 'text',
        adminComponent: 'AdminText',
        display_repeater_header: true,
    },
    avatar_media_id: {
        name: lang('congresses::congresses.schedule.speaker_avatar'),
        slug: 'avatar_media_id',
        type: 'file',
        adminComponent: 'AdminFile',
        accept: 'image/*',
    },
}));
</script>
